import { useCallback, useEffect, useRef, useState } from "react";

import { realtimeClient } from "services/realtime/client";
import { matchScope } from "services/realtime/matchScope";
import { RealtimeConnectionState, RealtimeScope, RealtimeTopic } from "services/realtime/types";

import { useAppSelector } from "store/hooks";

const DEBOUNCE_MS = 800;
const MAX_WAIT_MS = 5000;
const FALLBACK_POLL_MS = 30_000;

function useRealtime(topics: RealtimeTopic[], onInvalidate: () => void, scope?: RealtimeScope): void {
  const isModalActive = useAppSelector((state) => state.modal.active);
  const [connectionState, setConnectionState] = useState<RealtimeConnectionState>(realtimeClient.state);

  const callbackRef = useRef(onInvalidate);
  callbackRef.current = onInvalidate;

  const modalRef = useRef(isModalActive);
  modalRef.current = isModalActive;

  const pendingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstHitRef = useRef(0);

  const topicsKey = topics.join(",");
  const scopeKey = JSON.stringify(scope ?? {});

  const flush = useCallback(() => {
    debounceRef.current = null;
    firstHitRef.current = 0;

    if (modalRef.current) {
      pendingRef.current = true;
      return;
    }

    pendingRef.current = false;
    callbackRef.current();
  }, []);

  const schedule = useCallback(() => {
    const now = Date.now();
    if (firstHitRef.current === 0) firstHitRef.current = now;

    if (debounceRef.current !== null) clearTimeout(debounceRef.current);

    const elapsed = now - firstHitRef.current;
    const delay = Math.min(DEBOUNCE_MS, Math.max(0, MAX_WAIT_MS - elapsed));
    debounceRef.current = setTimeout(flush, delay);
  }, [flush]);

  useEffect(() => {
    setConnectionState(realtimeClient.state);
    return realtimeClient.onStateChange(setConnectionState);
  }, []);

  useEffect(() => {
    const subscribedTopics = topicsKey.split(",");
    const filter = JSON.parse(scopeKey) as RealtimeScope;

    return realtimeClient.subscribe((signal) => {
      if (signal.topic !== "*") {
        if (!subscribedTopics.includes(signal.topic)) return;
        if (!matchScope(filter, signal.scope)) return;
      }
      schedule();
    });
  }, [topicsKey, scopeKey, schedule]);

  useEffect(() => {
    if (connectionState === "ready") return undefined;

    const timer = setInterval(schedule, FALLBACK_POLL_MS);
    return () => clearInterval(timer);
  }, [connectionState, schedule]);

  useEffect(() => {
    if (isModalActive || !pendingRef.current) return;
    schedule();
  }, [isModalActive, schedule]);

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, []);
}

export default useRealtime;
